import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Layout from '../components/Layout/Layout';
import axios from '../api/axiosConfig';

// API functions
const fetchWorkOrder = async (id) => {
  const { data } = await axios.get(`/workorders/${id}`);
  return data;
};

const createWorkOrder = async (data) => {
  const { data: res } = await axios.post('/workorders', data);
  return res;
};

const updateWorkOrder = async ({ id, data }) => {
  const { data: res } = await axios.patch(`/workorders/${id}`, data);
  return res;
};

const fetchClients = async () => {
  const { data } = await axios.get('/clients');
  return data;
};

const fetchVehicles = async (clientId) => {
  if (!clientId) return [];
  const { data } = await axios.get('/vehicles', { params: { clientId } });
  return data;
};

const fetchMechanics = async () => {
  const { data } = await axios.get('/users?role=mechanic');
  return data;
};

const WorkOrderFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'maintenance',
    clientId: '',
    vehicleId: '',
    mechanicId: '',
    startTime: new Date(),
    endTime: new Date(new Date().setHours(new Date().getHours() + 2)),
    notes: '',
    location: '',
    mileage: '',
    generatePdf: false,
    documentFormat: 'pdf',
  });
  const [error, setError] = useState('');

  const { data: workOrder, isLoading: isLoadingWorkOrder } = useQuery({
    queryKey: ['workorder', id],
    queryFn: () => fetchWorkOrder(id),
    enabled: isEdit,
  });

  const { data: clients } = useQuery({ queryKey: ['clients'], queryFn: fetchClients });
  const { data: vehicles } = useQuery({
    queryKey: ['vehicles', form.clientId],
    queryFn: () => fetchVehicles(form.clientId),
    enabled: !!form.clientId,
  });
  const { data: mechanics } = useQuery({ queryKey: ['mechanics'], queryFn: fetchMechanics });

  useEffect(() => {
    if (workOrder && isEdit) {
      setForm({
        title: workOrder.title || '',
        description: workOrder.description || '',
        type: workOrder.type || 'maintenance',
        clientId: workOrder.clientId?._id || workOrder.clientId || '',
        vehicleId: workOrder.vehicleId?._id || workOrder.vehicleId || '',
        mechanicId: workOrder.mechanicId?._id || workOrder.mechanicId || '',
        startTime: new Date(workOrder.startTime),
        endTime: new Date(workOrder.endTime),
        notes: workOrder.notes || '',
        location: workOrder.location || '',
        mileage: workOrder.mileage || '',
        generatePdf: false,
        documentFormat: 'pdf',
      });
    }
  }, [workOrder, isEdit]);

  const mutation = useMutation({
      mutationFn: isEdit ? updateWorkOrder : createWorkOrder,
      onSuccess: async (data) => {
        queryClient.invalidateQueries({ queryKey: ['workorders'] });

        if (form.generatePdf && data._id) {
          let urlPath = '';
          let fileExtension = '';
          
          if (form.documentFormat === 'pdf') {
            urlPath = `/api/workorders/${data._id}/pdf`;
            fileExtension = 'pdf';
          } else if (form.documentFormat === 'docx') {
            urlPath = `/api/workorders/${data._id}/docx`;
            fileExtension = 'docx';
          } else if (form.documentFormat === 'docx-template') {
            urlPath = `/api/workorders/${data._id}/docx-template`;
            fileExtension = 'docx';
          } else {
            return;
          }

          try {
            const token = localStorage.getItem('token');
            console.log('Запрос на генерацию:', urlPath);
            const response = await fetch(urlPath, {
              headers: { Authorization: `Bearer ${token}` },
            });

            if (response.ok) {
              const blob = await response.blob();
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `workorder-${data._id}.${fileExtension}`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              console.log('Файл успешно сгенерирован и скачан');
            } else {
              console.error(`Ошибка генерации: ${response.status} ${response.statusText}`);
            }
          } catch (err) {
            console.error('Ошибка при запросе документа:', err);
          }
        }
        navigate('/workorders');
      },
      onError: (err) => setError(err.response?.data?.message || 'Failed to save work order'),
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title || !form.clientId || !form.vehicleId) {
      setError('Пожалуйста, заполните все обязательные поля');
      return;
    }
    mutation.mutate(isEdit ? { id, data: form } : form);
  };

  if (isEdit && isLoadingWorkOrder) return <Layout><CircularProgress /></Layout>;

  return (
    <Layout>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            {isEdit ? 'Редактирование наряда' : 'Создание наряда'}
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Заголовок" required
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Описание" multiline rows={3}
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select fullWidth label="Тип работ"
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                  >
                    <MenuItem value="maintenance">Техобслуживание</MenuItem>
                    <MenuItem value="repair">Ремонт</MenuItem>
                    <MenuItem value="installation">Установка</MenuItem>
                    <MenuItem value="removal">Демонтаж</MenuItem>
                    <MenuItem value="inspection">Осмотр</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select fullWidth label="Клиент" required
                    value={form.clientId}
                    onChange={e => setForm({ ...form, clientId: e.target.value, vehicleId: '' })}
                  >
                    <MenuItem value="">Выберите клиента</MenuItem>
                    {clients?.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select fullWidth label="Транспорт" required
                    value={form.vehicleId}
                    onChange={e => setForm({ ...form, vehicleId: e.target.value })}
                    disabled={!form.clientId}
                  >
                    <MenuItem value="">Выберите ТС</MenuItem>
                    {vehicles?.map(v => <MenuItem key={v._id} value={v._id}>{v.licensePlate} - {v.brand} {v.model}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select fullWidth label="Механик"
                    value={form.mechanicId}
                    onChange={e => setForm({ ...form, mechanicId: e.target.value })}
                  >
                    <MenuItem value="">Не назначен</MenuItem>
                    {mechanics?.map(m => <MenuItem key={m._id} value={m._id}>{m.fullName}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <DateTimePicker
                    label="Начало"
                    value={form.startTime}
                    onChange={(v) => setForm({ ...form, startTime: v })}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DateTimePicker
                    label="Окончание"
                    value={form.endTime}
                    onChange={(v) => setForm({ ...form, endTime: v })}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Город"
                    value={form.location}
                    onChange={e => setForm({ ...form, location: e.target.value })}
                    helperText="Необязательно"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Пробег (км)"
                    type="number"
                    value={form.mileage}
                    onChange={e => setForm({ ...form, mileage: e.target.value })}
                    helperText="Необязательно"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth label="Примечания"
                    multiline rows={2}
                    value={form.notes}
                    onChange={e => setForm({ ...form, notes: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.generatePdf}
                        onChange={e => setForm({ ...form, generatePdf: e.target.checked })}
                      />
                    }
                    label="Создать печатный документ"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Формат документа</FormLabel>
                    <RadioGroup
                      row
                      value={form.documentFormat}
                      onChange={(e) => setForm({ ...form, documentFormat: e.target.value })}
                    >
                      <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
                      <FormControlLabel value="docx" control={<Radio />} label="MS Word (DOCX)" />
                      <FormControlLabel value="docx-template" control={<Radio />} label="DOCX (по шаблону)" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => navigate('/workorders')}>Отмена</Button>
                  <Button type="submit" variant="contained" disabled={mutation.isPending}>
                    {mutation.isPending ? 'Сохранение...' : 'Сохранить'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </LocalizationProvider>
        </Paper>
      </Container>
    </Layout>
  );
};

export default WorkOrderFormPage;